﻿import React, { useEffect, useMemo, useState } from 'react'
import { Heading, Button, Text } from '../ui'

type RunStatus = 'success' | 'failed' | 'cancelled' | null

interface Process {
  id: string
  key: string
  name: string
  description?: string | null
  is_active: boolean
}

interface ProcessSchedule {
  id: string
  process_id: string
  description: string | null
  enabled: boolean
  priority?: number
  weekdays?: any
  hours?: any
  month_days?: any
  next_run_at: string | null
  last_run_at?: string | null
  last_run_status?: RunStatus
  json_parameters?: any
}

interface CalendarModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedules: ProcessSchedule[]
  processes: Process[]
}

function parseNumericArrayText(text?: string | null): number[] {
  if (!text || typeof text !== 'string') return []
  const matches = text.match(/\d+/g)
  return matches ? matches.map((n) => Number(n)) : []
}

function monthDaysAllowed(raw: unknown): number[] {
  const arr = Array.isArray(raw) ? (raw as number[]) : parseNumericArrayText(raw as string | null | undefined)
  return arr.filter((n) => n >= 1 && n <= 28)
}

function formatDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${day}`
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function monthEnd(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

type CalendarEvent = {
  time: number
  label: string
  id: string
  processId: string
  colorIndex: number
  priority: number
}

/** Paleta fixa (classes completas) para distinguir tipos de processo no calendário */
const PROCESS_EVENT_COLORS = [
  { bg: 'bg-primary-100 dark:bg-primary-900/50', text: 'text-primary-900 dark:text-primary-100', border: 'border-l-primary-500', dot: 'bg-primary-500' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/50', text: 'text-emerald-900 dark:text-emerald-100', border: 'border-l-emerald-500', dot: 'bg-emerald-500' },
  { bg: 'bg-violet-100 dark:bg-violet-900/50', text: 'text-violet-900 dark:text-violet-100', border: 'border-l-violet-500', dot: 'bg-violet-500' },
  { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-900 dark:text-amber-100', border: 'border-l-amber-500', dot: 'bg-amber-500' },
  { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-900 dark:text-rose-100', border: 'border-l-rose-500', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/50', text: 'text-cyan-900 dark:text-cyan-100', border: 'border-l-cyan-500', dot: 'bg-cyan-500' },
  { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-900 dark:text-orange-100', border: 'border-l-orange-500', dot: 'bg-orange-500' },
  { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/50', text: 'text-fuchsia-900 dark:text-fuchsia-100', border: 'border-l-fuchsia-500', dot: 'bg-fuchsia-500' },
  { bg: 'bg-lime-100 dark:bg-lime-900/50', text: 'text-lime-900 dark:text-lime-100', border: 'border-l-lime-600', dot: 'bg-lime-600' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/50', text: 'text-indigo-900 dark:text-indigo-100', border: 'border-l-indigo-500', dot: 'bg-indigo-500' },
  { bg: 'bg-teal-100 dark:bg-teal-900/50', text: 'text-teal-900 dark:text-teal-100', border: 'border-l-teal-500', dot: 'bg-teal-500' },
  { bg: 'bg-sky-100 dark:bg-sky-900/50', text: 'text-sky-900 dark:text-sky-100', border: 'border-l-sky-500', dot: 'bg-sky-500' },
  { bg: 'bg-pink-100 dark:bg-pink-900/50', text: 'text-pink-900 dark:text-pink-100', border: 'border-l-pink-500', dot: 'bg-pink-500' },
  { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-900 dark:text-red-100', border: 'border-l-red-500', dot: 'bg-red-500' },
  { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-900 dark:text-yellow-100', border: 'border-l-yellow-500', dot: 'bg-yellow-500' },
  { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-900 dark:text-green-100', border: 'border-l-green-600', dot: 'bg-green-600' },
  { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-900 dark:text-purple-100', border: 'border-l-purple-500', dot: 'bg-purple-500' },
  { bg: 'bg-stone-100 dark:bg-stone-900/50', text: 'text-stone-900 dark:text-stone-100', border: 'border-l-stone-500', dot: 'bg-stone-500' },
  { bg: 'bg-slate-100 dark:bg-slate-900/50', text: 'text-slate-900 dark:text-slate-100', border: 'border-l-slate-500', dot: 'bg-slate-500' },
  { bg: 'bg-zinc-100 dark:bg-zinc-900/50', text: 'text-zinc-900 dark:text-zinc-100', border: 'border-l-zinc-500', dot: 'bg-zinc-500' },
  { bg: 'bg-neutral-100 dark:bg-neutral-900/50', text: 'text-neutral-900 dark:text-neutral-100', border: 'border-l-neutral-500', dot: 'bg-neutral-500' },
  { bg: 'bg-teal-200 dark:bg-teal-800/60', text: 'text-teal-950 dark:text-teal-50', border: 'border-l-teal-700', dot: 'bg-teal-700' },
  { bg: 'bg-pink-200 dark:bg-pink-800/60', text: 'text-pink-950 dark:text-pink-50', border: 'border-l-pink-700', dot: 'bg-pink-700' },
  { bg: 'bg-amber-200 dark:bg-amber-800/60', text: 'text-amber-950 dark:text-amber-50', border: 'border-l-amber-700', dot: 'bg-amber-700' },
] as const

function getProcessColorIndex(processId: string, sortedProcessIds: string[]): number {
  const idx = sortedProcessIds.indexOf(processId)
  if (idx < 0) return 0
  return idx % PROCESS_EVENT_COLORS.length
}

function eventColorClasses(colorIndex: number) {
  return PROCESS_EVENT_COLORS[colorIndex % PROCESS_EVENT_COLORS.length]
}

const MonthGrid: React.FC<{
  title: string
  start: Date
  end: Date
  itemsByDate: Map<string, CalendarEvent[]>
  onOpenDay: (dateKey: string) => void
}> = ({ title, start, end, itemsByDate, onOpenDay }) => {
  const days: Date[] = useMemo(() => {
    const list: Date[] = []
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) list.push(new Date(d))
    return list
  }, [start, end])

  const leadingBlanks = start.getDay() // 0..6 (Dom..Sáb)
  const todayKey = useMemo(() => formatDateKey(new Date()), [])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <Heading as="h4" size="2xl">{title}</Heading>
      </div>
      <div className="grid grid-cols-7 gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
        <div>Dom</div><div>Seg</div><div>Ter</div><div>Qua</div><div>Qui</div><div>Sex</div><div>Sáb</div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} className="p-2" />
        ))}
        {days.map((d) => {
          const key = formatDateKey(d)
          const items = itemsByDate.get(key) || []
          const isToday = key === todayKey
          return (
            <div
              key={key}
              className={
                `border rounded p-2 min-h-[90px] flex flex-col cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ` +
                (isToday
                  ? 'border-primary-500 ring-2 ring-primary-500 bg-primary-50 dark:border-primary-400 dark:ring-primary-400 dark:bg-primary-900/30'
                  : 'border-gray-200 dark:border-gray-700')
              }
              onClick={() => onOpenDay(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenDay(key) }}
            >
              <div className={`text-sm font-medium ${isToday ? 'text-primary-700 dark:text-primary-200' : 'text-gray-700 dark:text-gray-200'}`}>{d.getDate()}</div>
              <ul className="mt-1 space-y-0.5">
                {items.slice(0, 3).map((ev) => {
                  const c = eventColorClasses(ev.colorIndex)
                  return (
                    <li
                      key={ev.id + '-' + ev.time}
                      className={`truncate text-xs rounded px-1 py-0.5 border-l-2 ${c.bg} ${c.text} ${c.border}`}
                    >
                      <span className="font-bold">{ev.time.toString().padStart(2, '0')}:00</span> — {ev.label}
                    </li>
                  )
                })}
                {items.length > 3 && (
                  <li className="text-xs text-gray-500 dark:text-gray-400">+{items.length - 3} mais</li>
                )}
              </ul>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const CalendarModal: React.FC<CalendarModalProps> = ({ open, onOpenChange, schedules, processes }) => {
  const processById = useMemo(() => {
    const m = new Map<string, Process>()
    processes.forEach(p => m.set(p.id, p))
    return m
  }, [processes])

  const sortedProcessIds = useMemo(
    () => [...processes].map(p => p.id).sort(),
    [processes]
  )

  const [viewMonth, setViewMonth] = useState<Date>(() => monthStart(new Date()))
  const [dayModalDateKey, setDayModalDateKey] = useState<string | null>(null)
  const startRange = viewMonth
  const endRange = monthEnd(viewMonth)

  const itemsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()

    const allDates: Date[] = []
    for (let d = new Date(startRange); d <= endRange; d = addDays(d, 1)) allDates.push(new Date(d))

    const parsedSchedules = schedules.map((s) => {
      const weekdays = Array.isArray(s.weekdays) ? (s.weekdays as number[]) : parseNumericArrayText(s.weekdays)
      const hours = Array.isArray(s.hours) ? (s.hours as number[]) : parseNumericArrayText(s.hours)
      const monthDays = monthDaysAllowed((s as any).month_days)
      return { s, weekdays, hours, monthDays }
    })

    for (const date of allDates) {
      const dow = date.getDay()
      const dom = date.getDate()
      for (const { s, weekdays, hours, monthDays } of parsedSchedules) {
        if (!s.enabled) continue
        const weekdayOk = weekdays.length === 0 || weekdays.includes(dow)
        const monthOk = monthDays.length === 0 || monthDays.includes(dom)
        if (!weekdayOk || !monthOk) continue
        const proc = processById.get(s.process_id)
        const label = s.description ? s.description : (proc?.name || proc?.key || 'Agendamento')
        const colorIndex = getProcessColorIndex(s.process_id, sortedProcessIds)
        for (const h of hours) {
          const key = formatDateKey(date)
          if (!map.has(key)) map.set(key, [])
          map.get(key)!.push({
            time: h,
            label,
            id: s.id,
            processId: s.process_id,
            colorIndex,
            priority: typeof s.priority === 'number' && Number.isFinite(s.priority) ? Math.min(5, Math.max(0, Math.trunc(s.priority))) : 0,
          })
        }
      }
    }

    // Ordenar cada dia por hora; no mesmo horário, maior prioridade primeiro
    map.forEach((arr, k) => {
      arr.sort((a, b) => {
        if (a.time !== b.time) return a.time - b.time
        return b.priority - a.priority
      })
      map.set(k, arr)
    })

    return map
  }, [schedules, processById, sortedProcessIds, startRange, endRange])

  // Sempre que abrir o modal, voltar para o mês atual
  useEffect(() => {
    if (open) {
      setViewMonth(monthStart(new Date()))
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dayModalDateKey) {
          setDayModalDateKey(null)
        } else {
          onOpenChange(false)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange, dayModalDateKey])

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => onOpenChange(false)}>
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setViewMonth(prev => monthStart(new Date(prev.getFullYear(), prev.getMonth() - 1, 1)))}>Anterior</Button>
            <Heading as="h3" size="2xl">Calendário de agendamentos</Heading>
            <Button variant="outline" onClick={() => setViewMonth(prev => monthStart(new Date(prev.getFullYear(), prev.getMonth() + 1, 1)))}>Próximo</Button>
          </div>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <MonthGrid
            title={viewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            start={startRange}
            end={endRange}
            itemsByDate={itemsByDate}
            onOpenDay={(dateKey) => setDayModalDateKey(dateKey)}
          />
        </div>
        {dayModalDateKey && (
          <div className="fixed inset-0 flex items-center justify-center" onClick={() => setDayModalDateKey(null)}>
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg w-full max-w-3xl min-h-[55vh] max-h-[85vh] overflow-y-auto p-6 shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <Heading as="h4" size="2xl">
                  {(() => { const [y, m, d] = dayModalDateKey.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: '2-digit', month: 'long', year: 'numeric' }) })()}
                </Heading>
                <Button variant="ghost" onClick={() => setDayModalDateKey(null)}>Fechar</Button>
              </div>
              <div className="space-y-2">
                {(() => {
                  const items = itemsByDate.get(dayModalDateKey) || []
                  if (items.length === 0) return <Text size="sm" color="muted">Sem agendamentos</Text>
                  return (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {items.map(ev => {
                        const c = eventColorClasses(ev.colorIndex)
                        return (
                          <li
                            key={ev.id + '-' + ev.time}
                            className={`py-2 px-2 text-sm break-words rounded border-l-4 ${c.bg} ${c.text} ${c.border}`}
                          >
                            <span className="font-mono font-bold mr-2">{ev.time.toString().padStart(2, '0')}:00</span>
                            <span>{ev.label}</span>
                          </li>
                        )
                      })}
                    </ul>
                  )
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CalendarModal


